/**
 * Email Validation Utility
 *
 * Provides comprehensive email validation including:
 * - Format validation (RFC 5322 compliant regex)
 * - DNS MX record verification (checks if domain can receive emails)
 */

/**
 * Validates email format using RFC 5322 compliant regex
 * @param {string} email - Email address to validate
 * @returns {boolean} - True if format is valid
 */
export function isValidEmailFormat(email) {
  if (!email || typeof email !== 'string') {
    return false;
  }

  // RFC 5322 compliant email regex
  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

  return emailRegex.test(email.trim());
}

/**
 * Checks if domain has valid MX records using Cloudflare DNS-over-HTTPS
 * @param {string} domain - Domain to check (e.g., "gmail.com")
 * @returns {Promise<boolean>} - True if domain has MX records
 */
export async function hasMXRecords(domain) {
  try {
    // Use Cloudflare's public DNS-over-HTTPS API
    const response = await fetch(
      `https://cloudflare-dns.com/dns-query?name=${encodeURIComponent(domain)}&type=MX`,
      {
        headers: {
          'Accept': 'application/dns-json'
        }
      }
    );

    if (!response.ok) {
      console.warn('DNS query failed, skipping MX check');
      return true; // Fail open - don't block if DNS service is down
    }

    const data = await response.json();

    // Check if we got MX records in the response
    return data.Answer && data.Answer.length > 0;
  } catch (error) {
    console.warn('Error checking MX records:', error);
    return true; // Fail open - don't block on network errors
  }
}

/**
 * Comprehensive email validation (format + DNS check)
 * @param {string} email - Email address to validate
 * @returns {Promise<{isValid: boolean, error: string|null}>}
 */
export async function validateEmail(email) {
  // 1. Check format
  if (!isValidEmailFormat(email)) {
    return {
      isValid: false,
      error: 'Please enter a valid email address.'
    };
  }

  // Extract domain from email
  const domain = email.trim().split('@')[1];

  if (!domain) {
    return {
      isValid: false,
      error: 'Please enter a valid email address.'
    };
  }

  // 2. Check MX records
  const hasMX = await hasMXRecords(domain);

  if (!hasMX) {
    return {
      isValid: false,
      error: 'This email domain cannot receive emails. Please check your email address.'
    };
  }

  return {
    isValid: true,
    error: null
  };
}

/**
 * Quick synchronous email format check (no DNS validation)
 * Use this for real-time input validation
 * @param {string} email - Email address to validate
 * @returns {boolean} - True if format is valid
 */
export function quickValidateEmail(email) {
  return isValidEmailFormat(email);
}
